import React from "react";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { useManualContext } from "../context/ManualContext";
import { ChapterItem } from "./ChapterItem";
import { useConfirmation } from "context/Confirmation";

export const ChapterListV2: React.FC = () => {
  const { t } = useTranslation();
  const { state, selectChapter, addChapter, deleteChapter, reorderChapters } =
    useManualContext();
  const { openConfirmation } = useConfirmation();

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // If dropped outside the list or no movement
    if (
      !destination ||
      (source.index === destination.index &&
        source.droppableId === destination.droppableId)
    ) {
      return;
    }

    const newOrder = Array.from(state.chapterOrder);
    const [movedId] = newOrder.splice(source.index, 1);
    newOrder.splice(destination.index, 0, movedId);

    reorderChapters(newOrder);
  };

  const handleAddChapter = () => {
    const chapterName = t("manual.chapter.add_chapter");
    addChapter(chapterName);
  };

  const handleDeleteChapter = (chapterId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    openConfirmation(() => deleteChapter(chapterId), {
      label: t("form.basic.label"),
      subline: t("manual.delete.confirm_subline"),
    });
  };

  return (
    <div className="w-1/4 bg-white rounded-lg shadow p-4 overflow-y-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">{t("manual.chapter.label")}</h2>
        <button
          onClick={handleAddChapter}
          className="px-3 py-1 text-sm rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
        >
          + {t("manual.chapter.add_chapter")}
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="chapters" type="CHAPTER">
          {(provided) => (
            <ul
              className="space-y-2"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {state.chapterOrder.map((chapterId, index) => {
                const chapter = state.chapters[chapterId];
                if (!chapter) return null;

                return (
                  <ChapterItem
                    key={chapter.id}
                    chapter={chapter}
                    index={index}
                    isSelected={state.selectedChapterId === chapter.id}
                    onSelect={() => selectChapter(chapter.id)}
                    onDelete={(e) => handleDeleteChapter(chapter.id, e)}
                  />
                );
              })}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
